import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, BarChart3, ShieldCheck } from "lucide-react";

function formatTodayFr() {
  return new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const VALUES = [
  {
    icon: BookOpen,
    eyebrow: "Pédagogie",
    title: "Le programme, chapitre par chapitre",
    body: "Chaque professeur valide ses chapitres en trois temps : démarrage, évaluation, achèvement. Aucune zone d'ombre."
  },
  {
    icon: BarChart3,
    eyebrow: "Vision",
    title: "Une lecture en une minute",
    body: "L'administration consulte l'état de chaque classe en un regard : barres vertes, oranges, rouges — l'essentiel sans le bruit."
  },
  {
    icon: ShieldCheck,
    eyebrow: "Confiance",
    title: "Un suivi fidèle au calendrier",
    body: "Les attendus de l'année rythment naturellement l'avancement. Les retards sont visibles avant qu'ils ne s'installent."
  }
];

function Hero() {
  return (
    <section className="relative">
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/1290141/pexels-photo-1290141.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Bibliothèque élégante"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1010]/85 via-[#1a1010]/55 to-[#1a1010]/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3 text-white" data-testid="landing-logo">
          <div className="bg-white rounded-full p-1.5 shadow-lg">
            <img
              src="/lafayette-logo.jpg"
              alt="Lafayette Progress"
              className="h-11 w-11 object-contain rounded-full"
            />
          </div>
          <span className="font-serif-display text-2xl sm:text-3xl font-semibold tracking-tight">LAFAYETTE PROGRESS</span>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 lg:py-44">
        <div className="max-w-3xl text-white">
          <div className="eyebrow !text-white/70 mb-6">Collège & Lycée Lafayette</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight">
            Un programme suivi,<br />
            <span className="italic text-[#e6c89a]">une promesse tenue.</span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">
            Lafayette accompagne ses enseignants et son administration dans le pilotage rigoureux
            du programme annuel. Une vision claire de l'avancement, par classe et par matière —
            chapitre après chapitre.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/login" className="btn-primary !bg-white !text-[var(--maroon)] hover:!bg-[var(--parchment)]" data-testid="hero-login-btn">
              Accéder au tableau de bord <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesStrip() {
  return (
    <section id="about" className="border-b border-[var(--parchment-2)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-12">
        {VALUES.map(({ icon: Icon, eyebrow, title, body }) => (
          <div key={title} className="flex flex-col gap-4">
            <Icon className="text-[var(--maroon)]" size={28} strokeWidth={1.5} />
            <div className="eyebrow">{eyebrow}</div>
            <h3 className="font-serif-display text-2xl font-semibold leading-tight">{title}</h3>
            <p className="text-[var(--ink-soft)] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StorySection({ todayLabel }) {
  return (
    <section className="bg-[var(--parchment)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="eyebrow mb-4">L'espace enseignant</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl font-semibold leading-tight">
            Chaque professeur tient sa partition.
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-lg leading-relaxed">
            Connexion individuelle. Vos matières, vos classes, votre programme annuel.
            Vous validez le démarrage d'un chapitre, vous y associez une évaluation, vous le clôturez.
            Aucun nouveau chapitre ne s'ouvre tant que le précédent n'est pas achevé.
          </p>
          <ul className="mt-8 space-y-3 text-[var(--ink)]">
            <li className="flex gap-3"><span className="text-[var(--maroon)]">—</span> Validation en trois étapes par chapitre</li>
            <li className="flex gap-3"><span className="text-[var(--maroon)]">—</span> Progression visible par couleur (verte, orange, rouge)</li>
            <li className="flex gap-3"><span className="text-[var(--maroon)]">—</span> Comparaison avec le calendrier de l'année</li>
          </ul>
        </div>
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/8197553/pexels-photo-8197553.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=900"
            alt="Professeur en classe"
            className="w-full h-[500px] object-cover"
          />
          <div className="absolute -bottom-6 -left-6 bg-[var(--maroon)] text-white px-6 py-4" data-testid="story-week-badge">
            <div className="eyebrow !text-white/70">Au {todayLabel}</div>
            <div className="font-serif-display text-2xl">Année scolaire 2026-2027</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="bg-[var(--maroon-deep)] text-white/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col sm:flex-row justify-between gap-6">
        <div>
          <div className="font-serif-display text-2xl text-white">LAFAYETTE PROGRESS</div>
          <div className="eyebrow !text-white/50 mt-1">Tableau de bord pédagogique</div>
        </div>
        <div className="text-sm self-center">
          © 2026 — Tous droits réservés
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const todayLabel = formatTodayFr();
  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <Hero />
      <ValuesStrip />
      <StorySection todayLabel={todayLabel} />
      <LandingFooter />
    </div>
  );
}
